import {useCallback, useState} from 'react'
import {UseFormSetValue, useFormContext} from 'react-hook-form'
import {useTranslation} from '@/translations/i18n.tsx'
import {PlusIcon, XCloseIcon} from '@/components/ui/icon/Icon'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {InputText} from '@/components/commons/input-text/InputText'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {Switch} from '@/components/commons/switch/Switch'
import {MailerFormValues} from '@/features/mailer/types'
import {
    MailerMultiSelect,
    MailerMultiSelectOption
} from '@/features/mailer/components/mailer-multi-select/MailerMultiSelect'
import {usePathologyOptions} from '@/features/mailer/hooks/usePathologyOptions'
import {useNeuromuscularDiagnosis} from '@/features/patients/services/useNeuromuscularDiagnosis'
import {useGetDistricts} from '@/services/useGetDistricts'
import {
    StyledAddPathologyButton,
    StyledFieldsGrid,
    StyledFilterSection,
    StyledGatedField,
    StyledInlineBadgeLabel,
    StyledPriSubGroup,
    StyledRemovablePathology,
    StyledRoleBadge,
    StyledSectionHeading,
    StyledToggleBlock
} from '@/features/mailer/components/patient-filters-panel/style'

// Empty number inputs must reach the schema as undefined, not NaN/'' — the
// age schema is a plain z.number() (no preprocess) to dodge a zod 3.22 quirk
// where effects swallow check errors while sibling fields are invalid.
const ageValueAs = (value: unknown): number | undefined => {
    if (value === '' || value === null || value === undefined) return undefined
    const parsed = Number(value)
    return Number.isNaN(parsed) ? undefined : parsed
}

// Patient filters per functional spec §4: Profilo clinico → Demografia.
// "Solo pazienti fuori regione" is still BE-gated (no region filter key,
// OPSSTD-3643 §7): rendered disabled with tooltip.
export const PatientFiltersPanel = () => {
    const {t} = useTranslation()
    const {
        watch,
        setValue,
        register,
        formState: {errors}
    } = useFormContext<MailerFormValues>()

    const values = watch()

    const neuromuscularQuery = useNeuromuscularDiagnosis({responseType: 'small', limit: 100})
    const districtsQuery = useGetDistricts({responseType: 'small', limit: 100})

    // The diagnoses domain is server-searched (11k+ records): each multi-select
    // owns its query, while a shared value→label cache keeps chips readable
    // when the selection is no longer part of the current search results.
    const [pathologyLabels, setPathologyLabels] = useState<Record<string, string>>({})
    const registerPathologyLabels = useCallback((options: MailerMultiSelectOption[]) => {
        setPathologyLabels(previous => {
            const missing = options.filter(option => !(option.value in previous))
            if (missing.length === 0) return previous
            return {...previous, ...Object.fromEntries(missing.map(option => [option.value, option.label]))}
        })
    }, [])

    const rehabPathology1 = usePathologyOptions({enabled: values.priEnabled, onOptionsLoaded: registerPathologyLabels})
    const rehabPathology2 = usePathologyOptions({
        enabled: values.priEnabled && values.rehabPathology2Enabled,
        onOptionsLoaded: registerPathologyLabels
    })
    const otherPathology1 = usePathologyOptions({enabled: values.priEnabled, onOptionsLoaded: registerPathologyLabels})
    const otherPathology2 = usePathologyOptions({
        enabled: values.priEnabled && values.otherPathology2Enabled,
        onOptionsLoaded: registerPathologyLabels
    })

    const neuromuscularOptions = neuromuscularQuery.remappedData.map(diagnosis => ({
        value: diagnosis.id,
        label: diagnosis.name
    }))
    const aslOptions = districtsQuery.remappedData.map(district => ({
        value: district.id,
        label: `${district.name} (${district.aslName})`
    }))

    // Public signature is fully typed (UseFormSetValue); the internal cast works
    // around RHF's conditional PathValue types not distributing through wrappers.
    const setField: UseFormSetValue<MailerFormValues> = (name, value) =>
        setValue(name, value as never, {shouldValidate: true, shouldDirty: true})

    // Turning a toggle OFF resets every selection of its block (spec §13:
    // no hidden "ghost filters").
    const handleToggleDiagnosis = () => {
        if (values.diagnosisEnabled) setField('neuromuscularDiagnosisIds', [])
        setField('diagnosisEnabled', !values.diagnosisEnabled)
    }

    const handleTogglePri = () => {
        if (values.priEnabled) {
            setField('rehabPathology1Ids', [])
            setField('rehabPathology2Enabled', false)
            setField('rehabPathology2Ids', [])
            setField('otherPathology1Ids', [])
            setField('otherPathology2Enabled', false)
            setField('otherPathology2Ids', [])
        }
        setField('priEnabled', !values.priEnabled)
    }

    const pathology1Label = (
        <StyledInlineBadgeLabel>
            {t('mailer:pathology_1_label')}
            <StyledRoleBadge $accent="indigo">{t('mailer:pathology_1_badge')}</StyledRoleBadge>
        </StyledInlineBadgeLabel>
    )
    const pathology2Label = (
        <StyledInlineBadgeLabel>
            {t('mailer:pathology_2_label')}
            <StyledRoleBadge $accent="purple">{t('mailer:pathology_2_badge')}</StyledRoleBadge>
        </StyledInlineBadgeLabel>
    )

    return (
        <>
            <StyledFilterSection>
                <StyledSectionHeading>{t('mailer:section_clinical_profile')}</StyledSectionHeading>
                <StyledFieldsGrid $columns={3}>
                    <MailerMultiSelect
                        label={t('mailer:section_consent')}
                        placeholder={t('mailer:multiselect_placeholder_all')}
                        value={values.patientConsent}
                        onChange={value => setField('patientConsent', value as MailerFormValues['patientConsent'])}
                        options={[
                            {value: 'orientation', label: t('mailer:consent_orientation')},
                            {value: 'notifications', label: t('mailer:consent_notifications')},
                            {value: 'survey', label: t('mailer:consent_survey')}
                        ]}
                    />
                    <InputSelect
                        label={t('mailer:sector_label')}
                        value={values.patientSector}
                        onChange={value => setField('patientSector', value)}
                        optionsMap={{
                            all: 'mailer:sector_all',
                            ambulatorial: 'mailer:sector_ambulatorial',
                            home: 'mailer:sector_home'
                        }}
                    />
                    <InputSelect
                        label={t('mailer:service_type_label')}
                        value={values.patientServiceType}
                        onChange={value => setField('patientServiceType', value)}
                        optionsMap={{
                            all: 'mailer:service_type_all',
                            adults: 'mailer:service_type_adults',
                            evolutionary_age: 'mailer:service_type_evolutionary_age'
                        }}
                    />
                </StyledFieldsGrid>

                <StyledToggleBlock>
                    <Switch
                        label={t('mailer:toggle_diagnosis')}
                        checked={values.diagnosisEnabled}
                        onChange={handleToggleDiagnosis}
                    />
                    {values.diagnosisEnabled && (
                        <MailerMultiSelect
                            label={
                                <StyledInlineBadgeLabel>
                                    {t('mailer:diagnosis_label')}
                                    <StyledRoleBadge $accent="teal">{t('mailer:diagnosis_badge')}</StyledRoleBadge>
                                </StyledInlineBadgeLabel>
                            }
                            placeholder={t('mailer:multiselect_placeholder_all')}
                            accent="teal"
                            value={values.neuromuscularDiagnosisIds}
                            onChange={value => setField('neuromuscularDiagnosisIds', value)}
                            options={neuromuscularOptions}
                        />
                    )}
                </StyledToggleBlock>

                <StyledToggleBlock>
                    <Switch label={t('mailer:toggle_pri')} checked={values.priEnabled} onChange={handleTogglePri} />
                    {values.priEnabled && (
                        <>
                            <StyledPriSubGroup>
                                <strong>{t('mailer:pri_rehab_group')}</strong>
                                <StyledFieldsGrid $columns={2}>
                                    <MailerMultiSelect
                                        label={pathology1Label}
                                        placeholder={t('mailer:multiselect_placeholder_all')}
                                        accent="indigo"
                                        value={values.rehabPathology1Ids}
                                        onChange={value => setField('rehabPathology1Ids', value)}
                                        selectedLabelByValue={pathologyLabels}
                                        {...rehabPathology1}
                                    />
                                    <Tooltip content={t('mailer:not_available_yet')} triggerProps={{asChild: true}}>
                                        <StyledGatedField>
                                            <MailerMultiSelect
                                                label={t('mailer:pri_icd10_label')}
                                                placeholder={t('mailer:multiselect_placeholder_all')}
                                                disabled
                                                value={[]}
                                                onChange={() => undefined}
                                                options={[]}
                                            />
                                        </StyledGatedField>
                                    </Tooltip>
                                </StyledFieldsGrid>
                                {!values.rehabPathology2Enabled ? (
                                    <StyledAddPathologyButton
                                        type="button"
                                        onClick={() => setField('rehabPathology2Enabled', true)}
                                    >
                                        <PlusIcon />
                                        {t('mailer:add_pathology_2')}
                                    </StyledAddPathologyButton>
                                ) : (
                                    <StyledRemovablePathology>
                                        <button
                                            type="button"
                                            title={t('mailer:remove_pathology_2')}
                                            aria-label={t('mailer:remove_pathology_2')}
                                            onClick={() => {
                                                setField('rehabPathology2Enabled', false)
                                                setField('rehabPathology2Ids', [])
                                            }}
                                        >
                                            <XCloseIcon />
                                        </button>
                                        <MailerMultiSelect
                                            label={pathology2Label}
                                            placeholder={t('mailer:multiselect_placeholder_all')}
                                            accent="purple"
                                            value={values.rehabPathology2Ids}
                                            onChange={value => setField('rehabPathology2Ids', value)}
                                            selectedLabelByValue={pathologyLabels}
                                            {...rehabPathology2}
                                        />
                                    </StyledRemovablePathology>
                                )}
                            </StyledPriSubGroup>

                            <StyledPriSubGroup>
                                <strong>{t('mailer:pri_other_group')}</strong>
                                <StyledFieldsGrid $columns={2}>
                                    <MailerMultiSelect
                                        label={pathology1Label}
                                        placeholder={t('mailer:multiselect_placeholder_all')}
                                        accent="indigo"
                                        value={values.otherPathology1Ids}
                                        onChange={value => setField('otherPathology1Ids', value)}
                                        selectedLabelByValue={pathologyLabels}
                                        {...otherPathology1}
                                    />
                                    <Tooltip content={t('mailer:not_available_yet')} triggerProps={{asChild: true}}>
                                        <StyledGatedField>
                                            <MailerMultiSelect
                                                label={t('mailer:pri_icd10_label')}
                                                placeholder={t('mailer:multiselect_placeholder_all')}
                                                disabled
                                                value={[]}
                                                onChange={() => undefined}
                                                options={[]}
                                            />
                                        </StyledGatedField>
                                    </Tooltip>
                                </StyledFieldsGrid>
                                {!values.otherPathology2Enabled ? (
                                    <StyledAddPathologyButton
                                        type="button"
                                        onClick={() => setField('otherPathology2Enabled', true)}
                                    >
                                        <PlusIcon />
                                        {t('mailer:add_pathology_2')}
                                    </StyledAddPathologyButton>
                                ) : (
                                    <StyledRemovablePathology>
                                        <button
                                            type="button"
                                            title={t('mailer:remove_pathology_2')}
                                            aria-label={t('mailer:remove_pathology_2')}
                                            onClick={() => {
                                                setField('otherPathology2Enabled', false)
                                                setField('otherPathology2Ids', [])
                                            }}
                                        >
                                            <XCloseIcon />
                                        </button>
                                        <MailerMultiSelect
                                            label={pathology2Label}
                                            placeholder={t('mailer:multiselect_placeholder_all')}
                                            accent="purple"
                                            value={values.otherPathology2Ids}
                                            onChange={value => setField('otherPathology2Ids', value)}
                                            selectedLabelByValue={pathologyLabels}
                                            {...otherPathology2}
                                        />
                                    </StyledRemovablePathology>
                                )}
                            </StyledPriSubGroup>
                        </>
                    )}
                </StyledToggleBlock>
            </StyledFilterSection>

            <StyledFilterSection $withSeparator>
                <StyledSectionHeading>{t('mailer:section_demography')}</StyledSectionHeading>
                <StyledFieldsGrid $columns={4}>
                    <InputText
                        type="number"
                        label={t('mailer:age_min')}
                        placeholder="0"
                        min={0}
                        max={100}
                        errorMessage={errors.patientMinAge?.message}
                        touched
                        {...register('patientMinAge', {setValueAs: ageValueAs, deps: ['patientMaxAge']})}
                    />
                    <InputText
                        type="number"
                        label={t('mailer:age_max')}
                        placeholder="100"
                        min={0}
                        max={100}
                        errorMessage={errors.patientMaxAge?.message}
                        touched
                        {...register('patientMaxAge', {setValueAs: ageValueAs, deps: ['patientMinAge']})}
                    />
                    <InputSelect
                        label={t('mailer:gender_label')}
                        value={values.patientGender}
                        onChange={value => setField('patientGender', value)}
                        optionsMap={{
                            all: 'mailer:gender_all',
                            male: 'mailer:gender_male',
                            female: 'mailer:gender_female'
                        }}
                    />
                    <MailerMultiSelect
                        label={t('mailer:asl_label')}
                        placeholder={t('mailer:multiselect_placeholder_all')}
                        value={values.patientAslDistrictIds}
                        onChange={value => setField('patientAslDistrictIds', value)}
                        options={aslOptions}
                    />
                </StyledFieldsGrid>
                <Tooltip content={t('mailer:not_available_yet')} triggerProps={{asChild: true}}>
                    <StyledGatedField $inline>
                        <Checkbox
                            id="mailer-outside-region"
                            label={t('mailer:outside_region')}
                            checked={values.patientOutsideRegion}
                            disabled
                            onChange={() => undefined}
                        />
                    </StyledGatedField>
                </Tooltip>
            </StyledFilterSection>
        </>
    )
}

PatientFiltersPanel.displayName = 'PatientFiltersPanel'
